import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy } from '@angular/core';
import { WorkspaceIconsModule } from '../../shared/workspace-icons.module';
import { Subscription } from 'rxjs';
import { FileItem, FilesService } from './files.service';

/** Visible image thumbnails only; the catalogue never downloads every file just to list it. */
@Component({
  selector: 'app-file-thumbnail', standalone: true, imports: [CommonModule, WorkspaceIconsModule],
  template: `<img *ngIf="url; else symbol" [src]="url" alt="" draggable="false" (error)="clear()">
    <ng-template #symbol>
      <br-icon *ngIf="file.isFolder; else fileSymbol" aria-hidden="true" class="folder" svgIcon="br:folder"></br-icon>
      <ng-template #fileSymbol><br-icon aria-hidden="true">{{ icon }}</br-icon></ng-template>
    </ng-template>`,
  // Hosts may enlarge the symbol (for example Grid tiles) through --file-thumbnail-size.
  styles: [`:host { --size: var(--file-thumbnail-size, var(--br-space-5)); display: flex; align-items: center; justify-content: center; flex: 0 0 var(--size); width: var(--size); height: var(--size); overflow: hidden; }
    img { width: 100%; height: 100%; object-fit: contain; }
    br-icon { width: var(--size); height: var(--size); font-size: var(--size); color: var(--br-color-primary); }
    .folder { color: var(--br-color-folder); }`],
})
export class FileThumbnailComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) file!: FileItem;
  url: string | null = null;
  private observer?: IntersectionObserver;
  private request?: Subscription;
  constructor(private host: ElementRef<HTMLElement>, private files: FilesService) {}
  get icon(): string {
    if (this.file.contentType === 'application/pdf') return 'picture_as_pdf';
    if (this.file.contentType?.startsWith('image/')) return 'image';
    return 'description';
  }
  ngOnChanges(): void {
    this.clear();
    if (this.file.isFolder || this.file.status !== 'Ready' ||
        !['image/png', 'image/jpeg'].includes(this.file.contentType ?? '') ||
        !this.file.byteSize || this.file.byteSize > 2 * 1024 * 1024) return;
    const observer = new IntersectionObserver(entries => {
      if (this.observer !== observer || !entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect(); this.observer = undefined;
      this.request = this.files.content(this.file.id).subscribe({
        next: blob => {
          if (['image/png', 'image/jpeg'].includes(blob.type)) this.url = URL.createObjectURL(blob);
        },
        error: () => { /* Keep the file-type icon when a thumbnail is unavailable. */ },
      });
    });
    this.observer = observer;
    observer.observe(this.host.nativeElement);
  }
  clear(): void {
    this.observer?.disconnect(); this.observer = undefined;
    this.request?.unsubscribe(); this.request = undefined;
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = null;
  }
  ngOnDestroy(): void { this.clear(); }
}
